import React from "react";
import { radarInitials, radarTint } from "./radarFormat";
import { radarLabelHex } from "../../constants/agentRadarChoices";

// Shared visual atoms for the Agent Radar surface, pixel-matched to the design
// source. Real player data drives them: a photo/logo renders when the API supplies
// one, else a deterministic tinted-initials chip (never the prototype's samples).

// Player avatar — initials on a 12%-alpha tint, or the player photo when present.
export const RadarAvatar = ({ name, imageUrl, size = 44, fontSize, seed }) => {
  const base = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        referrerPolicy="no-referrer"
        style={{ ...base, objectFit: "cover" }}
      />
    );
  }
  const color = radarTint(seed || name);
  return (
    <div
      style={{
        ...base,
        background: `${color}1C`,
        color,
        fontWeight: 700,
        fontSize: fontSize || Math.round(size * 0.32),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {radarInitials(name)}
    </div>
  );
};

// Club crest — club logo when present, else solid-tint initials on white.
export const RadarCrest = ({ name, logoUrl, size = 16, seed }) => {
  const base = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        referrerPolicy="no-referrer"
        style={{ ...base, objectFit: "cover" }}
      />
    );
  }
  const color = radarTint(`${seed || name}-club`);
  return (
    <div
      style={{
        ...base,
        background: color,
        color: "#FFFFFF",
        fontSize: size * 0.46,
        fontWeight: 800,
        letterSpacing: "0.02em",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {radarInitials(name)}
    </div>
  );
};

// Colour label pill (design: 1px hue border @ 33% alpha, 8%-alpha fill, dot + name).
// `onRemove` adds the detail-panel ✕. Same anatomy as the card pill, no attribution.
export const RadarLabelPill = ({ label, onRemove }) => {
  const hex = radarLabelHex(label.color);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        border: `1px solid ${hex}55`,
        background: `${hex}14`,
        color: "#374151",
        borderRadius: 6,
        padding: "1px 7px",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: "17px",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: hex, flexShrink: 0, display: "inline-block" }} />
      {label.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label.name} label`}
          className="border-none bg-transparent text-[#9CA3AF] hover:text-[#374151] cursor-pointer leading-none"
          style={{ fontSize: 11, padding: "0 0 0 2px" }}
        >
          ✕
        </button>
      )}
    </span>
  );
};

// The "N notes" chip on a card (design: 1px #DDE3EB border, #F8FAFC fill).
export const RadarNotesChip = ({ children }) => (
  <span
    className="inline-flex items-center whitespace-nowrap flex-shrink-0"
    style={{
      border: "1px solid #DDE3EB",
      background: "#F8FAFC",
      color: "#6B7280",
      borderRadius: 6,
      padding: "1px 7px",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: "16px",
    }}
  >
    {children}
  </span>
);
